import { showAchievementToast } from '../components/achievements/achievement-toast';

// Keep track of achievements we've already shown to avoid duplicates
const shownAchievements = new Set<string>();

// Track the last time we checked for achievements
let lastCheckTime = new Date().getTime();

/**
 * Safely converts achievement data to an array
 * @param achievements - Achievement data that could be an array, object, or null/undefined
 * @returns Array of achievement objects
 */
function ensureAchievementArray(achievements: any): any[] {
  // If it's null or undefined, return empty array
  if (achievements == null) return [];
  
  // If it's already an array, return it
  if (Array.isArray(achievements)) return achievements;
  
  // If it's an object with an 'achievements' property that's an array
  if (achievements.achievements && Array.isArray(achievements.achievements)) {
    return achievements.achievements;
  }
  
  // If it's a single achievement object, wrap it in an array
  if (typeof achievements === 'object' && (achievements.achievement_id || achievements.id)) {
    return [achievements];
  }
  
  // Default to empty array
  return [];
}

/**
 * Handles new achievements by showing achievement toasts
 * @param achievements - Array of achievement objects from the API 
 */
export const handleNewAchievements = (achievements: any = []) => {
  // Ensure we have an array to work with
  const achievementsArray = ensureAchievementArray(achievements);
  
  if (achievementsArray.length === 0) return;
  
  // Get current time to filter for truly new achievements
  const currentTime = new Date().getTime();
  const oneHourAgo = currentTime - (60 * 60 * 1000);
  
  // Filter for TRULY new achievements:
  // 1. Those explicitly marked as is_new=true
  // 2. Those awarded since the last check time
  // 3. Those awarded within the last hour (as a fallback for first page load)
  const newAchievements = achievementsArray.filter(achievement => {
    if (!achievement) return false;
    
    // Skip if we don't have achievement ID or it's explicitly not new
    if (achievement.is_new === false) return false;
    
    // Is it explicitly marked as new?
    if (achievement.is_new === true) return true;
    
    // Check if it was awarded since we last checked
    if (achievement.awarded_at) {
      const awardedTime = new Date(achievement.awarded_at).getTime();
      
      // Award is newer than last check or within the last hour (for first page load)
      if (awardedTime > lastCheckTime || awardedTime > oneHourAgo) {
        // Create a unique ID for tracking
        const achievementId = achievement.achievement_id || achievement.id;
        const uniqueId = `${achievementId}-${achievement.awarded_at}`;
        
        // Only include if we haven't shown it already
        if (!shownAchievements.has(uniqueId)) {
          shownAchievements.add(uniqueId);
          return true;
        }
      }
    }
    
    return false;
  });
  
  // Update last check time
  lastCheckTime = currentTime;
  
  // Skip if no NEW achievements
  if (newAchievements.length === 0) return;
  
  console.log(`Found ${newAchievements.length} new achievements to display`);
  
  // Show toast for each NEW achievement
  newAchievements.forEach((achievement, index) => {
    // Show toast notification with a delay between each
    setTimeout(() => {
      showAchievementToast(achievement);
    }, index * 1000); // 1 second delay between each toast
  });
  
  // Optionally mark achievements as acknowledged in the backend
  if (newAchievements.length > 0) {
    markAchievementsAsAcknowledged(
      newAchievements.map(a => a.achievement_id || a.id)
    );
  }
};

/**
 * Marks achievements as acknowledged in the backend
 * @param achievementIds - Array of achievement IDs to mark as acknowledged
 */
const markAchievementsAsAcknowledged = async (achievementIds: number[]) => {
  if (!achievementIds || achievementIds.length === 0) return;
  
  try {
    const response = await fetch('/api/achievements/acknowledge', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ achievement_ids: achievementIds }),
      credentials: 'include'
    });
    
    if (!response.ok) {
      console.error('Failed to mark achievements as acknowledged');
    }
  } catch (error) {
    console.error('Error marking achievements as acknowledged:', error);
  }
};

/**
 * Helper function to check for and display achievement notifications
 * @param data API response data that might contain new achievements
 */
export function handleNewAchievementsFromData(data: any): void {
  if (!data) return;
  
  // Different APIs might return achievements in different formats
  const achievementsArray = 
    data?.new_achievements || 
    data?.achievements || 
    data?.user_achievements || 
    [];
  
  // Use our main handler with extra safety
  handleNewAchievements(achievementsArray);
} 